import React, { useState } from "react";

import {
  AUTH_COPY,
  PROFILE_FIELD_LABEL,
  SEX_OPTIONS,
} from "./authenticationConstants";
import {
  composeDateOfBirth,
  dateOfBirthError,
} from "./authenticationPayload";
import "./treatmentAuthentication.css";

/**
 * Ask only for the eligibility fields the backend reported missing (C5, EX-05).
 *
 * This is deliberately not a demographics screen: an existing patient whose
 * profile only lacks a date of birth sees one date input, not their name, phone,
 * sex, and address replayed back at them.
 */
export function MissingProfileFieldsStep({
  missingFields = [],
  pending = false,
  fieldErrors = null,
  onSubmit,
}) {
  const [values, setValues] = useState({ month: "", day: "", year: "", sex: "" });
  const [errors, setErrors] = useState({});

  const setField = (name) => (event) => {
    setValues((current) => ({ ...current, [name]: event.target.value }));
  };
  const setNumericField = (name, maxLength) => (event) => {
    const value = event.target.value.replace(/\D/g, "").slice(0, maxLength);
    setValues((current) => ({ ...current, [name]: value }));
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    if (pending) return;
    const payload = {};
    const found = {};

    if (missingFields.includes("date_of_birth")) {
      const dateOfBirth = composeDateOfBirth(values);
      const dobError = dateOfBirthError(dateOfBirth);
      if (dobError) {
        found.date_of_birth = dobError;
      } else {
        payload.date_of_birth = dateOfBirth;
      }
    }
    if (missingFields.includes("sex")) {
      if (!SEX_OPTIONS.some((option) => option.value === values.sex)) {
        found.sex = "Select the sex you were assigned at birth.";
      } else {
        payload.sex = values.sex;
      }
    }

    setErrors(found);
    if (Object.keys(found).length > 0) return;
    onSubmit(payload);
  };

  const errorFor = (name) => errors[name] || fieldErrors?.[name] || null;

  return (
    <form className="treatment-auth" onSubmit={handleSubmit} noValidate>
      <p className="treatment-auth__eyebrow">
        {AUTH_COPY.missingProfileStep.eyebrow}
      </p>
      <h1 className="treatment-auth__title">
        {AUTH_COPY.missingProfileStep.title}
      </h1>
      <p className="treatment-auth__description">
        {AUTH_COPY.missingProfileStep.description}
      </p>

      {missingFields.includes("date_of_birth") && (
        <fieldset className="treatment-auth__fieldset">
          <legend className="treatment-auth__label">
            {PROFILE_FIELD_LABEL.date_of_birth}
          </legend>
          <div className="treatment-auth__row treatment-auth__row--dob">
            <input
              aria-label="Month"
              className="treatment-auth__input"
              inputMode="numeric"
              maxLength={2}
              placeholder="MM"
              value={values.month}
              onChange={setNumericField("month", 2)}
              disabled={pending}
            />
            <input
              aria-label="Day"
              className="treatment-auth__input"
              inputMode="numeric"
              maxLength={2}
              placeholder="DD"
              value={values.day}
              onChange={setNumericField("day", 2)}
              disabled={pending}
            />
            <input
              aria-label="Year"
              className="treatment-auth__input"
              inputMode="numeric"
              maxLength={4}
              placeholder="YYYY"
              value={values.year}
              onChange={setNumericField("year", 4)}
              disabled={pending}
            />
          </div>
          {errorFor("date_of_birth") && (
            <p className="treatment-auth__error" role="alert">
              {errorFor("date_of_birth")}
            </p>
          )}
        </fieldset>
      )}

      {missingFields.includes("sex") && (
        <fieldset className="treatment-auth__fieldset">
          <legend className="treatment-auth__label">
            {PROFILE_FIELD_LABEL.sex}
          </legend>
          <div className="treatment-auth__row">
            {SEX_OPTIONS.map((option) => (
              <label key={option.value} className="treatment-auth__choice">
                <input
                  type="radio"
                  name="missing-sex"
                  value={option.value}
                  checked={values.sex === option.value}
                  onChange={setField("sex")}
                  disabled={pending}
                />
                {option.label}
              </label>
            ))}
          </div>
          {errorFor("sex") && (
            <p className="treatment-auth__error" role="alert">
              {errorFor("sex")}
            </p>
          )}
        </fieldset>
      )}

      <button type="submit" className="treatment-auth__submit" disabled={pending}>
        {pending
          ? AUTH_COPY.missingProfileStep.submitting
          : AUTH_COPY.missingProfileStep.submit}
      </button>
    </form>
  );
}

export default MissingProfileFieldsStep;
